import React, { useState } from 'react';
import { useQuery } from 'react-query';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import DataObjectIcon from '@mui/icons-material/DataObject';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { fetchLectureRecent } from 'utils/ts-queries';
import ReclamationSurBonDeVenteDetail from 'components/Shared/ReclamationSurBonDeVenteDetail';
import JsonViewerDialog from 'components/Shared/JsonViewerDialog';

/**
 * Page admin "Expérimental" : lecture brute d'une réclamation sur bon de vente (service RECENT).
 * Saisie d'une clé → appel GET /api/gescom/recent/{cle} → affichage du JSON brut renvoyé par Gescom.
 */
function AdministrationLectureRecent() {
    const [cleInput, setCleInput] = useState('');
    const [cleRecherchee, setCleRecherchee] = useState('');
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

    const { isFetching, isError, data, refetch } = useQuery(
        ['lectureRecent', cleRecherchee],
        () => fetchLectureRecent(cleRecherchee),
        {
            enabled: false,            // lecture à la demande (bouton)
            refetchOnWindowFocus: false,
        }
    );

    const lancerLecture = () => {
        const cle = cleInput.trim();
        if (!cle) return;
        setCleRecherchee(cle);
        // setState est asynchrone : on déclenche le refetch au prochain tick avec la nouvelle clé.
        setTimeout(() => refetch(), 0);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Lecture RECENT (expérimental)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Lecture brute d'une réclamation sur bon de vente dans Gescom (service RECENT), par clé.
                Le contenu affiché est la réponse JSON brute du service.
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <TextField
                    label="Clé RECENT"
                    value={cleInput}
                    onChange={(e) => setCleInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') lancerLecture(); }}
                    size="small"
                    sx={{ width: 320 }}
                />
                <Button
                    variant="contained"
                    onClick={lancerLecture}
                    disabled={!cleInput.trim() || isFetching}
                >
                    Lire
                </Button>
                {isFetching && <CircularProgress size={24} />}
            </Stack>

            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Erreur lors de l'appel au service.
                </Alert>
            )}

            {data && (
                <>
                    {data.erreur
                        ? <Alert severity="warning" sx={{ mb: 2 }}>{data.erreurMessage || 'Erreur Gescom'}</Alert>
                        : <Alert severity="success" sx={{ mb: 2 }}>
                            Lecture OK{data.cle ? ` — clé ${data.cle}` : ''}{typeof data.execTime === 'number' ? ` — ${data.execTime} ms` : ''}
                        </Alert>}

                    {!data.erreur && data.message && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <ReclamationSurBonDeVenteDetail value={data.message} />
                        </Paper>
                    )}

                    <Button
                        variant="outlined"
                        startIcon={<DataObjectIcon />}
                        onClick={() => setJsonDialogOpen(true)}
                        disabled={!data.message}
                    >
                        Voir le JSON
                    </Button>

                    <JsonViewerDialog
                        open={jsonDialogOpen}
                        onClose={() => setJsonDialogOpen(false)}
                        title={`Message JSON - RECENT ${data.cle || ''}`}
                        jsonEntries={[
                            { label: 'RECENT', value: data.message },
                        ]}
                    />
                </>
            )}
        </Box>
    );
}

export default AdministrationLectureRecent;
